import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import ChooseAgreement from 'components/ChooseAgreement/ChooseAgreement';
import DividerComponent from 'components/Divider/Divider';
import InfoBox from 'components/InfoBox/InfoBox';
import useOrderTag from 'hooks/tags/useOrderTag';
import { REGISTER_VEHICLE_SUMMARY } from 'routes/overview/RegisterVehicle/paths';
import { AgreementOption, agreementOption as agreementOptionAtom } from 'state/agreement';
import ChooseAddress from './ChooseAddress/ChooseAddress';
import ChooseSubscriptionPlan from './ChooseSubscriptionPlan/ChooseSubscriptionPlan';
import ChooseVehicles from './ChooseVehicle/ChooseVehicles';
import RegisterVehicleLayout from './RegisterVehicleLayout';
import FinancialSecurityDialog from './CreditCheckPopups/FinancialSecurityDialog';
import SentForManualApprovalDialog from './CreditCheckPopups/SentForManualApprovalDialog';
import { EnumRegistrationStatus } from 'api';
import { useSnackbar } from 'notistack';
import RegistrationPendingDialog from './CreditCheckPopups/RegistrationPendingDialog';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import { AGREEMENTS } from 'routes/overview/paths';
export default function RegisterVehicle() {
  const {
    t
  } = useTranslation();
  const {
    push
  } = useHistory();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    customer
  } = useCurrentCustomer();
  const {
    isLoading,
    orderTag,
    acceptDepositRegistration,
    rejectDepositRegistration,
    isOrderValid,
    resetState
  } = useOrderTag();
  const [agreementOption] = useRecoilState(agreementOptionAtom);
  const [deposit, setDeposit] = useState(undefined);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [openFinancialSecurityDialog, setOpenFinancialSecurityDialog] = useState(false);
  const [openSentForManualApprovalDialog, setOpenSentForManualApprovalDialog] = useState(false);
  const [openRegistrationPendingDialog, setOpenRegistrationPendingDialog] = useState(false);
  const onForwardButtonClick = async () => {
    try {
      const result = await orderTag(agreementOption === AgreementOption.NEW);
      switch (result.registrationStatus) {
        case EnumRegistrationStatus.Completed:
          push(REGISTER_VEHICLE_SUMMARY);
          break;
        case EnumRegistrationStatus.DepositRequired:
          setDeposit(result.deposit);
          setOpenFinancialSecurityDialog(true);
          break;
        case EnumRegistrationStatus.ManualReview:
          setOpenSentForManualApprovalDialog(true);
          break;
        case EnumRegistrationStatus.RegistrationPending:
          setOpenRegistrationPendingDialog(true);
          break;
        case EnumRegistrationStatus.Failed:
          enqueueSnackbar(t('error.something'), {
            variant: 'error'
          });
      }
    } catch (error) {
      enqueueSnackbar(t('error.something'), {
        variant: 'error'
      });
    }
  };
  useEffect(() => {
    return () => {
      resetState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onAcceptDeposit = async () => {
    const assessmentId = deposit?.creditAssessmentId;
    if (!customer?.id || !assessmentId) {
      enqueueSnackbar(t('error.something'), {
        variant: 'error'
      });
      return false;
    }
    setIsAccepting(true);
    try {
      await acceptDepositRegistration(assessmentId);
      return true;
    } catch (error) {
      enqueueSnackbar(t('error.something'), {
        variant: 'error'
      });
      return false;
    } finally {
      setIsAccepting(false);
    }
  };
  const onRejectDeposit = async () => {
    const assessmentId = deposit?.creditAssessmentId;
    if (!customer?.id || !assessmentId) {
      setOpenFinancialSecurityDialog(false);
      return;
    }
    setIsRejecting(true);
    try {
      await rejectDepositRegistration(assessmentId);
    } catch (error) {
      enqueueSnackbar(t('error.something'), {
        variant: 'error'
      });
    } finally {
      setIsRejecting(false);
      setOpenFinancialSecurityDialog(false);
    }
  };
  return /*#__PURE__*/React.createElement(RegisterVehicleLayout, {
    onForwardButtonClick: onForwardButtonClick,
    altForwardButtonText: t('register_vehicle.order_new_tag'),
    isForwardButtonLoading: isLoading,
    hasForwardButtonDisabled: !isOrderValid,
    isFinalStep: true
  }, /*#__PURE__*/React.createElement(DividerComponent, null), /*#__PURE__*/React.createElement(ChooseVehicles, null), /*#__PURE__*/React.createElement(DividerComponent, null), /*#__PURE__*/React.createElement(ChooseAgreement, null), /*#__PURE__*/React.createElement(DividerComponent, null), /*#__PURE__*/React.createElement(ChooseAddress, null), /*#__PURE__*/React.createElement(DividerComponent, null), /*#__PURE__*/React.createElement(ChooseSubscriptionPlan, null), /*#__PURE__*/React.createElement(DividerComponent, null), /*#__PURE__*/React.createElement(InfoBox, {
    warning: true,
    mb: 3
  }, t('register_vehicle.check_form_warning')), /*#__PURE__*/React.createElement(FinancialSecurityDialog, {
    key: deposit?.creditAssessmentId,
    open: openFinancialSecurityDialog,
    onPrimaryBtnClick: onAcceptDeposit,
    onDone: () => push(REGISTER_VEHICLE_SUMMARY),
    onClose: () => setOpenFinancialSecurityDialog(false),
    primaryBtnClickIsDisabled: isRejecting,
    isLoading: isAccepting,
    onSecondaryBtnClick: onRejectDeposit,
    secondaryBtnClickIsDisabled: isAccepting || isRejecting,
    deposit: deposit
  }), /*#__PURE__*/React.createElement(SentForManualApprovalDialog, {
    open: openSentForManualApprovalDialog,
    onPrimaryBtnClick: () => push(AGREEMENTS),
    onClose: () => setOpenSentForManualApprovalDialog(false)
  }), /*#__PURE__*/React.createElement(RegistrationPendingDialog, {
    open: openRegistrationPendingDialog,
    onPrimaryBtnClick: () => push(REGISTER_VEHICLE_SUMMARY),
    onClose: () => setOpenRegistrationPendingDialog(false)
  }));
}